/**
 * stores/folders.ts
 *
 * Folders state management store
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useDatabase } from '@/composables/useDatabase'
import { logger } from '@/utils/logger'
import type { Folder, GActorTimestamp, Timestamp } from '@/types/google'
// import { liveQuery } from 'dexie'

// Folders saved before the `active` flag existed have it undefined and count as active
export function isFolderActive (folder: Pick<Folder, 'active'>): boolean {
    return folder.active !== false
}

export const useFoldersStore = defineStore('folders', () => {
    // State
    const folders = ref<Folder[]>([])
    const activeFolder = ref<Folder | null>(null)
    const loading = ref(false)
    const loaded = ref(false)
    const error = ref<Error | null>(null)

    // Live query observable for real-time updates
    // const folders$ = liveQuery(() => db.folders.toArray())

    // Computed
    const folderCount = computed(() => folders.value.length)
    const activeFolderCount = computed(() => folders.value.filter(folder => isFolderActive(folder)).length)

    // Remove the foldersById computed property and add it as a function instead
    async function getFolderById (id: number): Promise<Folder | undefined> {
        const db = useDatabase()

        return await db.folders.where('id').equals(id).first()
        // return folders.value.find(folder => folder.id === id)
    }

    const foldersByGid = computed(() => {
        return folders.value.reduce((acc, folder) => {
            acc[folder.gid] = folder
            return acc
        }, {} as Record<string, Folder>)
    })

    // Actions
    async function loadFolders () {
        loading.value = true
        error.value = null

        try {
            const db = useDatabase()
            const allFolders = await db.folders.toArray()
            folders.value = allFolders
            // Keep activeFolder in sync with the newly loaded folders
            if (activeFolder.value) {
                const updated = allFolders.find(f => f.id === activeFolder.value?.id) ?? null
                if (updated) {
                    activeFolder.value = updated
                }
            }
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to load folders')
            logger.error('Error loading folders:', error_)
        } finally {
            loading.value = false
            loaded.value = true
        }
    }

    async function addFolder (folder: Omit<Folder, 'id'>) {
        try {
            const db = useDatabase()
            const id = await db.folders.add(folder as Folder)
            await loadFolders()
            return id
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to add folder')
            logger.error('Error adding folder:', error_)
            throw error_
        }
    }

    async function addFolders (newFolders: Omit<Folder, 'id'>[]) {
        try {
            const db = useDatabase()
            await db.folders.bulkAdd(newFolders as Folder[])
            await loadFolders()
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to add folders')
            logger.error('Error adding folders:', error_)
            throw error_
        }
    }

    async function updateFolder (id: number, updates: Partial<Folder>) {
        const db = useDatabase()
        try {
            await db.folders.update(id, updates)
            await loadFolders()
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to update folder')
            logger.error('Error updating folder:', error_)
            throw error_
        }
    }

    async function setFolderFromTs (id: number, fromTs: Timestamp) {
        const updates: Partial<Folder> = {
            fromTs,
        }

        try {
            const db = useDatabase()
            await db.folders.update(id, updates)
            await loadFolders()
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to setFolderFromTs')
            logger.error('Error updating folder:', error_)
            throw error_
        }
    }

    async function resetFolderSync (id: number) {
        const updates: Partial<Folder> = {
            fromTs: 0,
            toTs: 0,
            syncTime: 0,
            neverSynced: true,
            isSyncing: false,
            syncJob: undefined,
            fileCount: 0,
            updates: {
                newFiles: 0,
                delFiles: 0,
                posts: 0,
                ring: [],
                ts: Date.now(),
            },
            actorsUpdates: [],
        }

        try {
            const db = useDatabase()
            await db.folders.update(id, updates)
            await loadFolders()
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to update folder')
            logger.error('Error updating folder:', error_)
            throw error_
        }
    }

    async function deleteFolder (id: number) {
        try {
            const db = useDatabase()
            await db.folders.delete(id)
            await loadFolders()
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to delete folder')
            logger.error('Error deleting folder:', error_)
            throw error_
        }
    }

    async function getFolderByGid (gid: string) {
        try {
            const db = useDatabase()
            return await db.folders.where('gid').equals(gid).first()
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to get folder by gid')
            logger.error('Error getting folder by gid:', error_)
            throw error_
        }
    }

    async function clearAllFolders () {
        try {
            const db = useDatabase()
            await db.folders.clear()
            folders.value = []
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to clear folders')
            logger.error('Error clearing folders:', error_)
            throw error_
        }
    }

    async function setActiveFolder (id: number) {
        try {
            const folder = await getFolderById(id)
            activeFolder.value = folder || null
            return folder
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to set active folder')
            logger.error('Error setting active folder:', error_)
            activeFolder.value = null
            throw error_
        }
    }

    // Most recently synced first
    function bySyncTimeDesc (a: Folder, b: Folder) {
        return (b.syncTime - a.syncTime) || ((b.id ?? 0) - (a.id ?? 0))
    }

    /**
     * Keeps at most `maxActive` folders active, deactivating the least recently synced ones.
     * `null` means unlimited: every inactive folder is re-activated.
     */
    async function applyActiveFolderLimit (maxActive: number | null) {
        const db = useDatabase()
        const all = await db.folders.toArray()

        let changed = false

        if (maxActive === null) {
            for (const folder of all.filter(f => !isFolderActive(f))) {
                await db.folders.update(folder.id!, { active: true })
                changed = true
            }
        } else {
            const active = all.filter(f => isFolderActive(f)).sort(bySyncTimeDesc)

            for (const folder of active.slice(maxActive)) {
                await db.folders.update(folder.id!, { active: false })
                changed = true
            }
        }

        if (changed) {
            await loadFolders()
        }
    }

    /** Activates the most recently synced inactive folder, if there is one */
    async function activateMostRecentInactiveFolder () {
        const db = useDatabase()
        const inactive = (await db.folders.toArray()).filter(f => !isFolderActive(f)).sort(bySyncTimeDesc)

        if (inactive[0]?.id !== undefined) {
            await db.folders.update(inactive[0].id, { active: true })
            await loadFolders()
        }
    }

    async function appendActorsToFolder (id: number, actorsLastActionTime: GActorTimestamp[]) {
        // Fetch the folder
        const folder = await getFolderById(id)
        if (!folder) {
            throw new Error(`Folder with id ${id} not found`)
        }

        // Create array and populate with existing data from folder
        const ary: GActorTimestamp[] = folder.actorsLastActionTime ? [...folder.actorsLastActionTime] : []

        // Append data from actorsLastActionTime parameter
        for (const actorEntry of actorsLastActionTime) {
            // Find if this actorId already exists in ary
            const existingIndex = ary.findIndex(entry => entry.actor === actorEntry.actor)

            if (existingIndex === -1) {
                // ActorId doesn't exist - add new item
                ary.push({ actor: actorEntry.actor, ts: actorEntry.ts })
            } else {
                // ActorId exists - update if timestamp is newer
                const existingEntry = ary[existingIndex]
                if (existingEntry && actorEntry.ts > existingEntry.ts) {
                    existingEntry.ts = actorEntry.ts
                }
            }
        }

        // Create a sorted array in decreasing order of Timestamp
        ary.sort((a, b) => b.ts - a.ts)
        const actorIds = ary.map(entry => entry.actor)

        // Update the folder with the new actorsLastActionTime
        const updates: Partial<Folder> = {
            actorsLastActionTime: ary,
            actors: actorIds,
        }

        try {
            await updateFolder(id, updates)
        } catch (error_) {
            error.value = error_ instanceof Error ? error_ : new Error('Failed to append actors to folder')
            logger.error('Error appending actors to folder:', error_)
            throw error_
        }
    }

    // Resets in-memory state only (e.g. on logout); Dexie data is untouched
    function resetState () {
        folders.value = []
        activeFolder.value = null
        loading.value = false
        loaded.value = false
        error.value = null
    }

    return {
        // State
        folders,
        activeFolder,
        loading,
        loaded,
        error,
        resetState,
        // folders$,

        // Computed
        folderCount,
        activeFolderCount,
        getFolderById,
        foldersByGid,

        // Actions
        loadFolders,
        addFolder,
        addFolders,
        updateFolder,
        deleteFolder,
        getFolderByGid,
        clearAllFolders,
        setActiveFolder,
        resetFolderSync,
        setFolderFromTs,
        appendActorsToFolder,
        applyActiveFolderLimit,
        activateMostRecentInactiveFolder,
    }
})
